import axios from "axios";
import { API_ROOT } from ".";
import { getAuthUser } from "./auth";
import { PaymentStatus } from "../components/dashboard/utils/types";

const PICKUPS_API_URL = `${API_ROOT}/pickups`;

function getAuthHeaders(): { Authorization: string } {
  const token = getAuthUser()?.token;
  if (!token) throw new Error("Not authenticated");
  return { Authorization: `Bearer ${token}` };
}

export interface Pickup {
  id: string;
  userId: string;
  firstName: string;
  surname: string;
  phoneNumber: string;
  email: string;
  pickupLocationId: string;
  /** Location at CSA signup; farm signup cannot change location later. */
  signupPickupLocationId?: string;
  pickupDay: string;
  subscriptionTierId: string;
  subscriptionPaymentStatus: PaymentStatus;
  paidAt?: string;
  friendPickupFirstName?: string | null;
  friendPickupLastName?: string | null;
  friendPickupPhone?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePickupPayload {
  /** When provided (e.g. in UserPickup flow), backend may use it for admin "create for user X"; otherwise pickup is for the logged-in user. */
  userId?: string;
  pickupLocationId: string;
  pickupDay: string;
  subscriptionTierId: string;
  subscriptionPaymentStatus: PaymentStatus;
  friendPickupFirstName?: string | null;
  friendPickupLastName?: string | null;
  friendPickupPhone?: string | null;
}

export interface UpdatePickupPayload {
  pickupLocationId?: string;
  pickupDay?: string;
  subscriptionTierId?: string;
  subscriptionPaymentStatus?: PaymentStatus;
  friendPickupFirstName?: string | null;
  friendPickupLastName?: string | null;
  friendPickupPhone?: string | null;
}

export async function callCreateCsaCheckoutSession(): Promise<{
  checkoutUrl: string;
}> {
  const res = await axios.post<{ checkoutUrl: string }>(
    `${PICKUPS_API_URL}/checkout-session`,
    {},
    { headers: getAuthHeaders() },
  );
  return res.data;
}

export async function callFetchPickups(): Promise<Pickup[]> {
  const res = await axios.get<Pickup[]>(PICKUPS_API_URL, {
    headers: getAuthHeaders(),
  });
  return res.data;
}

export async function callFetchPickup(id: string): Promise<Pickup> {
  const res = await axios.get<Pickup>(`${PICKUPS_API_URL}/${id}`, {
    headers: getAuthHeaders(),
  });
  return res.data;
}

export async function callCreatePickup(
  data: CreatePickupPayload,
): Promise<Pickup> {
  const res = await axios.post<Pickup>(PICKUPS_API_URL, data, {
    headers: getAuthHeaders(),
  });
  return res.data;
}

export async function callUpdatePickup(
  id: string,
  data: UpdatePickupPayload,
): Promise<Pickup> {
  const res = await axios.put<Pickup>(`${PICKUPS_API_URL}/${id}`, data, {
    headers: getAuthHeaders(),
  });
  return res.data;
}

export async function callDeletePickup(id: string): Promise<void> {
  await axios.delete(`${PICKUPS_API_URL}/${id}`, {
    headers: getAuthHeaders(),
  });
}
